/**
 * Planned Recurring Events Service
 *
 * This service handles all API interactions for the planned recurring events feature,
 * including CRUD operations for planned events and participant management.
 */

import axios, { AxiosError } from 'axios';
import { API_BASE_URL } from '../utils/config';
import {
  PlannedRecurringEvent,
  CreatePlannedEvent,
  UpdatePlannedEvent,
  PlannedEventParticipant,
  WeeklySchedule,
  PlannedEventQuery,
  UserSummary
} from '../types/plannedEvents';

class PlannedEventService {
  private readonly baseUrl = `${API_BASE_URL}/api/planned-events`;

  /**
   * Get authentication header from localStorage
   */
  private getAuthHeader() {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  /**
   * Handle API errors consistently
   */
  private handleError(error: unknown, operation: string): never {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<{ message?: string; error?: string }>;
      const errorMessage = axiosError.response?.data?.message ||
                          axiosError.response?.data?.error ||
                          axiosError.message ||
                          'Unknown error occurred';
      console.error(`Error ${operation}:`, errorMessage, axiosError.response?.data);
      /*
       * [Web UX 2. csomag – 3. javító kör, F-1, 2026-10-03] A becsomagolt hiba MEGŐRZI a válasz
       * státuszát/konfigurációját. MI VOLT A BAJ: a sima `new Error(...)` elvesztette a `response`-t,
       * ezért a `PlannedEventDialog` lejárat-mondata (`sessionExpiredSaveMessage(error)`, 2. kör)
       * SOSEM futott: 401-nél az angol „updating planned event failed: Request failed with status
       * code 401" jelent meg az újra-belépő ablak mellett. A `.message` változatlan (a többi hívó
       * ezt mutatja), csak a `response`/`config`/`request`/`code` kerül rá.
       */
      const wrapped = new Error(`${operation} failed: ${errorMessage}`) as Error & {
        response?: unknown;
        config?: unknown;
        request?: unknown;
        code?: string;
      };
      wrapped.response = axiosError.response;
      wrapped.config = axiosError.config;
      wrapped.request = axiosError.request;
      wrapped.code = axiosError.code;
      throw wrapped;
    }
    console.error(`Error ${operation}:`, error);
    throw error;
  }

  /**
   * Unwrap backend response if it follows { success, data } pattern
   */
  private unwrapResponse<T>(data: any): T {
    if (data && typeof data === 'object' && 'success' in data && 'data' in data) {
      return data.data as T;
    }
    return data as T;
  }

  /**
   * Get all planned recurring events with optional filters
   */
  async getPlannedEvents(query?: PlannedEventQuery): Promise<PlannedRecurringEvent[]> {
    try {
      const response = await axios.get(this.baseUrl, {
        headers: this.getAuthHeader(),
        params: query
      });
      return this.unwrapResponse<PlannedRecurringEvent[]>(response.data);
    } catch (error) {
      return this.handleError(error, 'fetching planned events');
    }
  }

  /**
   * Get a single planned event by ID
   */
  async getPlannedEvent(plannedEventId: string): Promise<PlannedRecurringEvent> {
    try {
      const response = await axios.get(`${this.baseUrl}/${plannedEventId}`, {
        headers: this.getAuthHeader()
      });
      return this.unwrapResponse<PlannedRecurringEvent>(response.data);
    } catch (error) {
      return this.handleError(error, 'fetching planned event');
    }
  }

  /**
   * Create a new planned recurring event
   */
  async createPlannedEvent(event: CreatePlannedEvent): Promise<PlannedRecurringEvent> {
    try {
      const response = await axios.post(this.baseUrl, event, {
        headers: this.getAuthHeader()
      });
      return this.unwrapResponse<PlannedRecurringEvent>(response.data);
    } catch (error) {
      return this.handleError(error, 'creating planned event');
    }
  }

  /**
   * Update an existing planned recurring event
   */
  async updatePlannedEvent(
    plannedEventId: string,
    event: UpdatePlannedEvent
  ): Promise<PlannedRecurringEvent> {
    try {
      const response = await axios.put(`${this.baseUrl}/${plannedEventId}`, event, {
        headers: this.getAuthHeader()
      });
      return this.unwrapResponse<PlannedRecurringEvent>(response.data);
    } catch (error) {
      return this.handleError(error, 'updating planned event');
    }
  }

  /**
   * Delete a planned recurring event
   */
  async deletePlannedEvent(plannedEventId: string): Promise<void> {
    try {
      await axios.delete(`${this.baseUrl}/${plannedEventId}`, {
        headers: this.getAuthHeader()
      });
    } catch (error) {
      return this.handleError(error, 'deleting planned event');
    }
  }

  /**
   * Get weekly schedule organized by day of week
   */
  async getWeeklySchedule(query?: PlannedEventQuery): Promise<WeeklySchedule> {
    try {
      const response = await axios.get(`${this.baseUrl}/weekly`, {
        headers: this.getAuthHeader(),
        params: query
      });
      return this.unwrapResponse<WeeklySchedule>(response.data);
    } catch (error) {
      return this.handleError(error, 'fetching weekly schedule');
    }
  }

  /**
   * Add a participant to a planned event
   */
  async addParticipant(plannedEventId: string, userId: string): Promise<PlannedEventParticipant> {
    try {
      const response = await axios.post(
        `${this.baseUrl}/${plannedEventId}/participants`,
        { userId },
        {
          headers: this.getAuthHeader()
        }
      );
      return this.unwrapResponse<PlannedEventParticipant>(response.data);
    } catch (error) {
      return this.handleError(error, 'adding participant');
    }
  }

  /**
   * Remove a participant from a planned event
   */
  async removeParticipant(plannedEventId: string, userId: string): Promise<void> {
    try {
      await axios.delete(`${this.baseUrl}/${plannedEventId}/participants/${userId}`, {
        headers: this.getAuthHeader()
      });
    } catch (error) {
      return this.handleError(error, 'removing participant');
    }
  }

  /**
   * Get all participants for a planned event
   */
  async getParticipants(plannedEventId: string): Promise<UserSummary[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/${plannedEventId}/participants`, {
        headers: this.getAuthHeader()
      });
      return this.unwrapResponse<UserSummary[]>(response.data);
    } catch (error) {
      return this.handleError(error, 'fetching participants');
    }
  }

  /**
   * Bulk add multiple participants to a planned event
   */
  async addMultipleParticipants(
    plannedEventId: string,
    userIds: string[]
  ): Promise<PlannedEventParticipant[]> {
    try {
      const response = await axios.post(
        `${this.baseUrl}/${plannedEventId}/participants/bulk`,
        { userIds },
        {
          headers: this.getAuthHeader()
        }
      );
      return this.unwrapResponse<PlannedEventParticipant[]>(response.data);
    } catch (error) {
      return this.handleError(error, 'adding multiple participants');
    }
  }

  /**
   * Get planned events for a specific day of week
   */
  async getPlannedEventsByDay(dayOfWeek: string, query?: PlannedEventQuery): Promise<PlannedRecurringEvent[]> {
    try {
      const response = await axios.get(this.baseUrl, {
        headers: this.getAuthHeader(),
        params: { ...query, dayOfWeek }
      });
      return this.unwrapResponse<PlannedRecurringEvent[]>(response.data);
    } catch (error) {
      return this.handleError(error, `fetching planned events for ${dayOfWeek}`);
    }
  }

  /**
   * Toggle active status of a planned event
   */
  async toggleActiveStatus(plannedEventId: string): Promise<PlannedRecurringEvent> {
    try {
      const response = await axios.patch(
        `${this.baseUrl}/${plannedEventId}/toggle-active`,
        {},
        {
          headers: this.getAuthHeader()
        }
      );
      return this.unwrapResponse<PlannedRecurringEvent>(response.data);
    } catch (error) {
      return this.handleError(error, 'toggling planned event status');
    }
  }
}

// Helper utilities

/**
 * Day of week translation from English to Hungarian
 */
export const translateDayOfWeek = (day: string): string => {
  const translations: { [key: string]: string } = {
    'Monday': 'Hétfő',
    'Tuesday': 'Kedd',
    'Wednesday': 'Szerda',
    'Thursday': 'Csütörtök',
    'Friday': 'Péntek',
    'Saturday': 'Szombat',
    'Sunday': 'Vasárnap'
  };
  return translations[day] || day;
};

/**
 * Day of week translation from Hungarian to English
 */
export const translateDayOfWeekToEnglish = (day: string): string => {
  const translations: { [key: string]: string } = {
    'Hétfő': 'Monday',
    'Kedd': 'Tuesday',
    'Szerda': 'Wednesday',
    'Csütörtök': 'Thursday',
    'Péntek': 'Friday',
    'Szombat': 'Saturday',
    'Vasárnap': 'Sunday'
  };
  return translations[day] || day;
};

/**
 * Format time string to HH:mm format
 */
export const formatTime = (time: string): string => {
  if (!time) return '';
  // If time already in HH:mm format, return as is
  if (/^\d{2}:\d{2}$/.test(time)) return time;
  // If time in HH:mm:ss format, strip seconds
  if (/^\d{2}:\d{2}:\d{2}$/.test(time)) return time.substring(0, 5);
  // Try to parse and format
  try {
    const date = new Date(`2000-01-01T${time}`);
    return date.toTimeString().substring(0, 5);
  } catch {
    return time;
  }
};

/**
 * Get ordered days of week starting from Monday (weekdays only)
 */
export const getOrderedDaysOfWeek = (): string[] => {
  return ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
};

/**
 * Get ordered days of week in Hungarian starting from Monday (weekdays only)
 */
export const getOrderedDaysOfWeekHu = (): string[] => {
  return ['Hétfő', 'Kedd', 'Szerda', 'Csütörtök', 'Péntek'];
};

/**
 * Get day of week number (0 = Monday, 6 = Sunday)
 */
export const getDayOfWeekNumber = (day: string): number => {
  const days = getOrderedDaysOfWeek();
  return days.indexOf(day);
};

/**
 * Sort planned events by start time
 */
export const sortByStartTime = (events: PlannedRecurringEvent[]): PlannedRecurringEvent[] => {
  return [...events].sort((a, b) => {
    const timeA = a.startTime.replace(':', '');
    const timeB = b.startTime.replace(':', '');
    return parseInt(timeA) - parseInt(timeB);
  });
};

/**
 * Group planned events by day of week (weekdays only)
 */
export const groupByDayOfWeek = (events: PlannedRecurringEvent[]): WeeklySchedule => {
  const schedule: WeeklySchedule = {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: []
  };

  events.forEach(event => {
    const day = event.dayOfWeek.toLowerCase() as keyof WeeklySchedule;
    if (schedule[day]) {
      schedule[day].push(event);
    }
  });

  // Sort each day's events by start time
  Object.keys(schedule).forEach(day => {
    schedule[day as keyof WeeklySchedule] = sortByStartTime(schedule[day as keyof WeeklySchedule]);
  });

  return schedule;
};

/**
 * Check if a planned event conflicts with another
 */
export const hasTimeConflict = (
  event1: { startTime: string; endTime: string },
  event2: { startTime: string; endTime: string }
): boolean => {
  const start1 = parseInt(event1.startTime.replace(':', ''));
  const end1 = parseInt(event1.endTime.replace(':', ''));
  const start2 = parseInt(event2.startTime.replace(':', ''));
  const end2 = parseInt(event2.endTime.replace(':', ''));

  // Check for overlap
  return (start1 < end2 && end1 > start2);
};

/**
 * Validate time range
 */
export const isValidTimeRange = (startTime: string, endTime: string): boolean => {
  const start = parseInt(startTime.replace(':', ''));
  const end = parseInt(endTime.replace(':', ''));
  return start < end;
};

/**
 * Calculate duration in minutes
 */
export const calculateDuration = (startTime: string, endTime: string): number => {
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);
  return (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
};

/**
 * Format duration to human readable string
 */
export const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins} perc`;
  if (mins === 0) return `${hours} óra`;
  return `${hours} óra ${mins} perc`;
};

// Export singleton instance
export default new PlannedEventService();
